import type {
  ClientOverviewRepository,
  GetRecentRevenueActivityParams,
  GetRecentExpenseActivityParams,
} from "../../domain/repositories/ClientOverviewRepository";
import type { RevenueConcentrationEntity } from "../../domain/entities/RevenueConcentrationEntity";
import type { ReceivablesAgingEntity } from "../../domain/entities/ReceivablesAgingEntity";
import type { LastMonthRevenueEntity } from "../../domain/entities/LastMonthRevenueEntity";
import type { LastMonthRevenueInsightsEntity } from "../../domain/entities/LastMonthRevenueInsightsEntity";
import type { RevenueThisMonthEntity } from "../../domain/entities/RevenueThisMonthEntity";
import type { RevenueThisMonthInsightsEntity } from "../../domain/entities/RevenueThisMonthInsightsEntity";
import type { LastMonthExpenseEntity } from "../../domain/entities/LastMonthExpenseEntity";
import type { LastMonthExpenseInsightsEntity } from "../../domain/entities/LastMonthExpenseInsightsEntity";
import type { ExpenseThisMonthEntity } from "../../domain/entities/ExpenseThisMonthEntity";
import type { ExpenseThisMonthInsightsEntity } from "../../domain/entities/ExpenseThisMonthInsightsEntity";
import type { KeyMetricsRevenueEntity } from "../../domain/entities/KeyMetricsRevenueEntity";
import type { KeyMetricsExpenseEntity } from "../../domain/entities/KeyMetricsExpenseEntity";
import type { TwelveMonthRevenueTrendEntity } from "../../domain/entities/TwelveMonthRevenueTrendEntity";
import type { TwelveMonthRevenueInsightsEntity } from "../../domain/entities/TwelveMonthRevenueInsightsEntity";
import type { TwelveMonthExpenseTrendEntity } from "../../domain/entities/TwelveMonthExpenseTrendEntity";
import type { TwelveMonthExpenseInsightsEntity } from "../../domain/entities/TwelveMonthExpenseInsightsEntity";
import type { RecentRevenueActivityEntity } from "../../domain/entities/RecentRevenueActivityEntity";
import type { RecentExpenseActivityEntity } from "../../domain/entities/RecentExpenseActivityEntity";
import type { ExpenseBreakdownEntity } from "../../domain/entities/ExpenseBreakdownEntity";
import type { MonthAtGlanceInsightsEntity } from "../../domain/entities/MonthAtGlanceInsightsEntity";
import type { RevenueVsExpensesInsightsEntity } from "../../domain/entities/RevenueVsExpensesInsightsEntity";
import type { EbitdaMarginInsightsEntity } from "../../domain/entities/EbitdaMarginInsightsEntity";
import type { CashInOutInsightsEntity } from "../../domain/entities/CashInOutInsightsEntity";
import type { CurrentBalanceEntity } from "../../domain/entities/CurrentBalanceEntity";
import type { CurrentBalanceInsightsEntity } from "../../domain/entities/CurrentBalanceInsightsEntity";
import type { ClientOverviewBootstrapEntity } from "../../domain/entities/ClientOverviewBootstrapEntity";
import type { RevenueBootstrapEntity } from "../../domain/entities/RevenueBootstrapEntity";
import type { ExpenseBootstrapEntity } from "../../domain/entities/ExpenseBootstrapEntity";
import {
  getClientOverviewBootstrap,
  getRevenueBootstrap,
  getExpenseBootstrap,
  getCurrentBalance,
  getCurrentBalanceInsights,
  getMonthAtGlanceInsights,
  getRevenueVsExpensesInsights,
  getEbitdaMarginInsights,
  getCashInOutInsights,
  getRevenueConcentration,
  getReceivablesAging,
  getLastMonthRevenue,
  getLastMonthRevenueInsights,
  getRevenueThisMonth,
  getRevenueThisMonthInsights,
  getLastMonthExpense,
  getLastMonthExpenseInsights,
  getExpenseThisMonth,
  getExpenseThisMonthInsights,
  getKeyMetricsRevenue,
  getKeyMetricsExpense,
  getTwelveMonthRevenueTrend,
  getTwelveMonthRevenueInsights,
  getTwelveMonthExpenseTrend,
  getTwelveMonthExpenseInsights,
  getRecentRevenueActivity,
  getRecentExpenseActivity,
  getExpenseBreakdown as fetchExpenseBreakdown,
} from "../api/clientOverviewApi";
import { currentBalanceDtoToEntity } from "../mappers/currentBalanceMapper";
import { currentBalanceInsightsDtoToEntity } from "../mappers/currentBalanceInsightsMapper";
import { revenueConcentrationDtoToEntity } from "../mappers/revenueConcentrationMapper";
import { receivablesAgingDtoToEntity } from "../mappers/receivablesAgingMapper";
import { lastMonthRevenueDtoToEntity } from "../mappers/lastMonthRevenueMapper";
import { lastMonthRevenueInsightsDtoToEntity } from "../mappers/lastMonthRevenueInsightsMapper";
import { revenueThisMonthDtoToEntity } from "../mappers/revenueThisMonthMapper";
import { revenueThisMonthInsightsDtoToEntity } from "../mappers/revenueThisMonthInsightsMapper";
import { lastMonthExpenseDtoToEntity } from "../mappers/lastMonthExpenseMapper";
import { lastMonthExpenseInsightsDtoToEntity } from "../mappers/lastMonthExpenseInsightsMapper";
import { expenseThisMonthDtoToEntity } from "../mappers/expenseThisMonthMapper";
import { expenseThisMonthInsightsDtoToEntity } from "../mappers/expenseThisMonthInsightsMapper";
import { keyMetricsRevenueDtoToEntity } from "../mappers/keyMetricsRevenueMapper";
import { keyMetricsExpenseDtoToEntity } from "../mappers/keyMetricsExpenseMapper";
import { twelveMonthRevenueTrendDtoToEntity } from "../mappers/twelveMonthRevenueTrendMapper";
import { twelveMonthRevenueInsightsDtoToEntity } from "../mappers/twelveMonthRevenueInsightsMapper";
import { twelveMonthExpenseTrendDtoToEntity } from "../mappers/twelveMonthExpenseTrendMapper";
import { twelveMonthExpenseInsightsDtoToEntity } from "../mappers/twelveMonthExpenseInsightsMapper";
import { recentRevenueActivityDtoToEntity } from "../mappers/recentRevenueActivityMapper";
import { recentExpenseActivityDtoToEntity } from "../mappers/recentExpenseActivityMapper";
import { expenseBreakdownDtoToEntity } from "../mappers/expenseBreakdownMapper";
import { monthAtGlanceInsightsDtoToEntity } from "../mappers/monthAtGlanceInsightsMapper";
import { revenueVsExpensesInsightsDtoToEntity } from "../mappers/revenueVsExpensesInsightsMapper";
import { ebitdaMarginInsightsDtoToEntity } from "../mappers/ebitdaMarginInsightsMapper";
import { cashInOutInsightsDtoToEntity } from "../mappers/cashInOutInsightsMapper";
import { clientOverviewBootstrapDtoToEntity } from "../mappers/clientOverviewBootstrapMapper";
import { revenueBootstrapDtoToEntity } from "../mappers/revenueBootstrapMapper";
import { expenseBootstrapDtoToEntity } from "../mappers/expenseBootstrapMapper";

export class ClientOverviewRepositoryImpl implements ClientOverviewRepository {
  async getClientOverviewBootstrap(): Promise<ClientOverviewBootstrapEntity> {
    const dto = await getClientOverviewBootstrap();
    return clientOverviewBootstrapDtoToEntity(dto);
  }

  async getRevenueBootstrap(): Promise<RevenueBootstrapEntity> {
    const dto = await getRevenueBootstrap();
    return revenueBootstrapDtoToEntity(dto);
  }

  async getExpenseBootstrap(): Promise<ExpenseBootstrapEntity> {
    const dto = await getExpenseBootstrap();
    return expenseBootstrapDtoToEntity(dto);
  }

  async getCurrentBalance(): Promise<CurrentBalanceEntity> {
    const dto = await getCurrentBalance();
    return currentBalanceDtoToEntity(dto);
  }

  async getCurrentBalanceInsights(): Promise<CurrentBalanceInsightsEntity> {
    const dto = await getCurrentBalanceInsights();
    return currentBalanceInsightsDtoToEntity(dto);
  }

  async getMonthAtGlanceInsights(): Promise<MonthAtGlanceInsightsEntity> {
    const dto = await getMonthAtGlanceInsights();
    return monthAtGlanceInsightsDtoToEntity(dto);
  }

  async getRevenueVsExpensesInsights(): Promise<RevenueVsExpensesInsightsEntity> {
    const dto = await getRevenueVsExpensesInsights();
    return revenueVsExpensesInsightsDtoToEntity(dto);
  }

  async getEbitdaMarginInsights(): Promise<EbitdaMarginInsightsEntity> {
    const dto = await getEbitdaMarginInsights();
    return ebitdaMarginInsightsDtoToEntity(dto);
  }

  async getCashInOutInsights(): Promise<CashInOutInsightsEntity> {
    const dto = await getCashInOutInsights();
    return cashInOutInsightsDtoToEntity(dto);
  }

  async getRevenueConcentration(): Promise<RevenueConcentrationEntity> {
    const dto = await getRevenueConcentration();
    return revenueConcentrationDtoToEntity(dto);
  }

  async getReceivablesAging(): Promise<ReceivablesAgingEntity> {
    const dto = await getReceivablesAging();
    return receivablesAgingDtoToEntity(dto);
  }

  async getLastMonthRevenue(): Promise<LastMonthRevenueEntity> {
    const dto = await getLastMonthRevenue();
    return lastMonthRevenueDtoToEntity(dto);
  }

  async getLastMonthRevenueInsights(): Promise<LastMonthRevenueInsightsEntity> {
    const dto = await getLastMonthRevenueInsights();
    return lastMonthRevenueInsightsDtoToEntity(dto);
  }

  async getRevenueThisMonth(): Promise<RevenueThisMonthEntity> {
    const dto = await getRevenueThisMonth();
    return revenueThisMonthDtoToEntity(dto);
  }

  async getRevenueThisMonthInsights(): Promise<RevenueThisMonthInsightsEntity> {
    const dto = await getRevenueThisMonthInsights();
    return revenueThisMonthInsightsDtoToEntity(dto);
  }

  async getLastMonthExpense(): Promise<LastMonthExpenseEntity> {
    const dto = await getLastMonthExpense();
    return lastMonthExpenseDtoToEntity(dto);
  }

  async getLastMonthExpenseInsights(): Promise<LastMonthExpenseInsightsEntity> {
    const dto = await getLastMonthExpenseInsights();
    return lastMonthExpenseInsightsDtoToEntity(dto);
  }

  async getExpenseThisMonth(): Promise<ExpenseThisMonthEntity> {
    const dto = await getExpenseThisMonth();
    return expenseThisMonthDtoToEntity(dto);
  }

  async getExpenseThisMonthInsights(): Promise<ExpenseThisMonthInsightsEntity> {
    const dto = await getExpenseThisMonthInsights();
    return expenseThisMonthInsightsDtoToEntity(dto);
  }

  async getKeyMetricsRevenue(): Promise<KeyMetricsRevenueEntity> {
    const dto = await getKeyMetricsRevenue();
    return keyMetricsRevenueDtoToEntity(dto);
  }

  async getKeyMetricsExpense(): Promise<KeyMetricsExpenseEntity> {
    const dto = await getKeyMetricsExpense();
    return keyMetricsExpenseDtoToEntity(dto);
  }

  async getTwelveMonthRevenueTrend(): Promise<TwelveMonthRevenueTrendEntity> {
    const dto = await getTwelveMonthRevenueTrend();
    return twelveMonthRevenueTrendDtoToEntity(dto);
  }

  async getTwelveMonthRevenueInsights(): Promise<TwelveMonthRevenueInsightsEntity> {
    const dto = await getTwelveMonthRevenueInsights();
    return twelveMonthRevenueInsightsDtoToEntity(dto);
  }

  async getTwelveMonthExpenseTrend(): Promise<TwelveMonthExpenseTrendEntity> {
    const dto = await getTwelveMonthExpenseTrend();
    return twelveMonthExpenseTrendDtoToEntity(dto);
  }

  async getTwelveMonthExpenseInsights(): Promise<TwelveMonthExpenseInsightsEntity> {
    const dto = await getTwelveMonthExpenseInsights();
    return twelveMonthExpenseInsightsDtoToEntity(dto);
  }

  async getRecentRevenueActivity(
    params: GetRecentRevenueActivityParams,
  ): Promise<RecentRevenueActivityEntity> {
    const dto = await getRecentRevenueActivity(params);
    return recentRevenueActivityDtoToEntity(dto);
  }

  async getRecentExpenseActivity(
    params: GetRecentExpenseActivityParams,
  ): Promise<RecentExpenseActivityEntity> {
    const dto = await getRecentExpenseActivity(params);
    return recentExpenseActivityDtoToEntity(dto);
  }

  async getExpenseBreakdown(
    type: "vendor" | "category",
    timeframe: "ytd" | "last_month",
  ): Promise<ExpenseBreakdownEntity> {
    const dto = await fetchExpenseBreakdown(type, timeframe);
    return expenseBreakdownDtoToEntity(dto);
  }
}
